import type { ValorantRegion, ValorantServer } from 'generated/openapi';
import { ChartNoAxesColumnIncreasing, Wifi } from 'lucide-react';
import type { ComponentProps } from 'react';
import { memo, useMemo } from 'react';

import { getRegionDisplayName } from '@/features/games/valorant/utils/region';
import {
  getMutualServers,
  getServerDisplayName,
} from '@/features/games/valorant/utils/servers';
import { useGetMyProfile } from '@/features/profile/hooks/useGetMyProfile';
import { Separator } from '@/shared/components/ui/separator';
import { cn } from '@/shared/utils/cn';

export interface ProfileOverviewCardConnectionInfoProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  servers: ValorantServer[] | undefined;
  region: ValorantRegion;
  pingMs: number | undefined;
}

export const ProfileOverviewCardConnectionInfo = memo(
  function ProfileOverviewCardConnectionInfo({
    servers,
    region,
    pingMs,
    className,
    ...props
  }: ProfileOverviewCardConnectionInfoProps) {
    const { data: myServers } = useGetMyProfile({
      options: { select: data => data.profile?.valorantServers },
    });

    const serversText = useMemo(() => {
      const mutual = getMutualServers(myServers, servers);
      if (mutual.length > 0) {
        const names = mutual.map(getServerDisplayName);
        return names.join(', ');
      }

      return getRegionDisplayName(region);
    }, [myServers, servers, region]);

    if (!serversText && pingMs === undefined) {
      return null;
    }

    const showSeparator = serversText && pingMs !== undefined;

    return (
      <div
        className={cn(
          'bg-titan-900 flex items-center gap-2 rounded-xl px-2 py-3',
          className,
        )}
        {...props}
      >
        {pingMs !== undefined && (
          <div className="flex w-1/2 min-w-0 items-center justify-center gap-2">
            <Wifi className="text-cold-300 size-6 shrink-0" />
            <span className="text-cold-300 text-xs font-medium md:text-sm">
              {pingMs}ms
            </span>
          </div>
        )}
        {showSeparator && <Separator orientation="vertical" className="h-6" />}
        {serversText && (
          <div className="flex w-1/2 min-w-0 items-center justify-center gap-2">
            <ChartNoAxesColumnIncreasing className="text-cold-300 size-6 shrink-0" />
            <span className="text-cold-300 truncate text-xs font-medium md:text-sm">
              {serversText}
            </span>
          </div>
        )}
      </div>
    );
  },
);
